import {fetchWrapper} from "@/helpers/fetch-wrapper";

/**
 * Serwis do operacji na użytkownikach, aktywnościach i uprawnieniach.
 */
class Users {
    /**
     * Buduje query string dla historii aktywności na podstawie jawnych kryteriów.
     *
     * Kryteria pochodzą z „zamrożonego” formularza (store.applied), a nie z bieżącego stanu
     * pól — dzięki temu paginacja i raport zawsze dotyczą tego, co użytkownik wyszukał.
     *
     * @param {{userId:?number, eventType:?number, dateTimeStart:string, dateTimeEnd:string}} criteria
     *
     * @returns {string} Query string bez wiodącego znaku "?".
     */
    buildActivityQuery(criteria) {
        const params = new URLSearchParams();

        params.set("dateTimeStart", criteria.dateTimeStart);
        params.set("dateTimeEnd", criteria.dateTimeEnd);
        params.set("orderDirection", criteria.orderDirection ?? "desc");

        if (criteria.userId) {
            params.set("userId", String(criteria.userId));
        }
        // Brak eventType = wszystkie typy zdarzeń włączone na backendzie.
        if (criteria.eventType) {
            params.set("eventTypes", String(criteria.eventType));
        }

        return params.toString();
    }

    /**
     * Pobiera listę użytkowników z API (wszystkich, sortowanych rosnąco).
     */
    getUserList() {
        return fetchWrapper.getWithCount(`${process.env.VUE_APP_API_URL}/users?orderDirection=asc`);
    }

    /**
     * Pobiera jedną stronę historii aktywności użytkowników.
     *
     * Wszystkie typy zdarzeń (łącznie z sesjami VMS) obsługuje endpoint /login-to-locations,
     * który filtruje, sortuje i stronicuje po stronie bazy danych.
     *
     * @param {object} criteria Kryteria wyszukiwania (patrz buildActivityQuery).
     * @param {number} pageNum  Numer strony (od 1).
     * @param {number} pageSize Liczba rekordów na stronę.
     *
     * @returns {Promise<{totalCount:string, content:Array}>}
     */
    getUserActivityList(criteria, pageNum, pageSize) {
        const query = this.buildActivityQuery(criteria);

        return fetchWrapper.getWithCount(
            `${process.env.VUE_APP_API_URL}/login-to-locations?${query}&page=${pageNum}&limit=${pageSize}`
        );
    }

    /**
     * Zleca wygenerowanie raportu (ZIP z plikiem CSV) dla podanych kryteriów.
     *
     * @param {object} criteria Kryteria wyszukiwania (patrz buildActivityQuery).
     */
    getActivityReport(criteria) {
        return fetchWrapper.get(
            `${process.env.VUE_APP_API_URL}/login-to-locations/report?${this.buildActivityQuery(criteria)}`
        );
    }

    /**
     * Dodaje nowego użytkownika (POST).
     * @param {Object} objectData - dane użytkownika
     */
    addUser(objectData) {
        // console.log('objectdata: ', objectData);
        return fetchWrapper.post(`${process.env.VUE_APP_API_URL}/users`, objectData);
    }

    /**
     * Pobiera dane użytkownika po ID.
     */
    fetchUser(id) {
        return fetchWrapper.get(`${process.env.VUE_APP_API_URL}/users/${id}`);
    }

    /**
     * Aktualizuje użytkownika po ID (PUT).
     * @param {Number} id - ID użytkownika
     * @param {Object} userData - nowe dane
     */
    updateUser(id, userData) {
        return fetchWrapper.put(`${process.env.VUE_APP_API_URL}/users/${id}`, userData);
    }

    /**
     * Usuwa użytkowników (bulk delete przez POST).
     * @param {Array} ids - tablica ID do usunięcia
     */
    deleteUsers(ids) {
        return fetchWrapper.post(`${process.env.VUE_APP_API_URL}/users/bulk-delete`, ids);
    }

    /**
     * Usuwa sesję użytkownika z bazy (wymuszone wylogowanie).
     * @param {Number} userId - ID użytkownika
     */
    killUserSession(userId) {
        return fetchWrapper.delete(`${process.env.VUE_APP_API_URL}/users/${userId}/session`);
    }

    /**
     * Pobiera listę ról dostępnych w systemie (do formularzy, dropdownów, uprawnień).
     */
    getRolesList() {
        return fetchWrapper.get(`${process.env.VUE_APP_API_URL}/roles`);
    }

    /**
     * Pobiera raport logowań użytkowników w zadanym zakresie dat (ZIP z plikiem CSV).
     *
     * Wersja bez dodatkowych filtrów — używana przez widoki, które nie mają własnego
     * formularza kryteriów (zarządzanie użytkownikami, kalibracja).
     *
     * @param {string} dateFrom Data początkowa (ISO8601).
     * @param {string} dateTo   Data końcowa (ISO8601).
     */
    getZip(dateFrom, dateTo) {
        return this.getActivityReport({ dateTimeStart: dateFrom, dateTimeEnd: dateTo });
    }

    /**
     * Pobiera plik (np. ZIP, CSV, PDF) jako blob (do pobrania).
     */
    fetch(zip) {
        let zipNoStart = zip.includes('api/v1') ? zip.substr(7) : zip;
        return fetchWrapper.getBlob(process.env.VUE_APP_API_URL + zipNoStart);
    }
}

// Eksport singletonu serwisu
export default new Users();
